/**
 * Cognito sign-up and sign-in, spoken directly to the user pool.
 *
 * The user pool is the only identity store: sign-up, e-mail verification, and
 * sign-in all happen against it with the AWS SDK's Cognito client. These three
 * operations are unauthenticated API calls, so the client is created with
 * anonymous credentials rather than reaching for a credential chain that does
 * not exist in a browser.
 */

import type {
  CognitoIdentityProviderClient,
  InitiateAuthCommandOutput,
} from '@aws-sdk/client-cognito-identity-provider';

/**
 * The SDK is loaded on demand. Nobody browsing listings needs it, and it is
 * several times the size of the rest of the application, so it is fetched the
 * first time someone actually signs up or signs in.
 */
type Sdk = typeof import('@aws-sdk/client-cognito-identity-provider');

let sdkPromise: Promise<Sdk> | undefined;

function sdk(): Promise<Sdk> {
  sdkPromise ??= import('@aws-sdk/client-cognito-identity-provider');
  return sdkPromise;
}

export interface CognitoConfig {
  readonly region: string;
  readonly userPoolId: string;
  readonly userPoolClientId: string;
}

export interface SessionTokens {
  readonly idToken: string;
  readonly accessToken: string;
  readonly refreshToken: string;
  /** Epoch milliseconds at which the id token stops being accepted. */
  readonly expiresAt: number;
  readonly email: string;
}

let cached: { readonly region: string; readonly client: CognitoIdentityProviderClient } | undefined;

async function client(config: CognitoConfig): Promise<CognitoIdentityProviderClient> {
  if (cached?.region === config.region) return cached.client;
  const { CognitoIdentityProviderClient: Client } = await sdk();
  const created = new Client({
    region: config.region,
    // Sign-up and sign-in are unsigned operations; there is no caller identity
    // to resolve, and supplying empty credentials stops the SDK looking for one.
    credentials: { accessKeyId: '', secretAccessKey: '' },
  });
  cached = { region: config.region, client: created };
  return created;
}

/** A failure we can put in front of a customer without leaking internals. */
export class AuthError extends Error {
  readonly code: string;

  constructor(code: string, message: string) {
    super(message);
    this.name = 'AuthError';
    this.code = code;
  }
}

const MESSAGES: Readonly<Record<string, string>> = {
  UsernameExistsException: 'An account already exists for that email address. Sign in instead.',
  NotAuthorizedException: 'That email address and password do not match an account.',
  UserNotFoundException: 'That email address and password do not match an account.',
  UserNotConfirmedException: 'Confirm your email address to finish setting up your account.',
  CodeMismatchException: 'That confirmation code is not correct.',
  ExpiredCodeException: 'That confirmation code has expired. Request a new one.',
  InvalidPasswordException:
    'Choose a password of at least twelve characters, with upper case, lower case, and a number.',
  InvalidParameterException: 'Check the details you entered and try again.',
  LimitExceededException: 'Too many attempts. Wait a few minutes and try again.',
  TooManyRequestsException: 'Too many attempts. Wait a few minutes and try again.',
  // Without an entry here this arrives as the generic "That request failed."
  // and strands the account with nothing to act on.
  PasswordResetRequiredException: 'Your password must be reset before you can sign in.',
  CodeDeliveryFailureException:
    'We could not send the code to that address. Check it and try again.',
};

function toAuthError(error: unknown): AuthError {
  const name = (error as { name?: string } | null)?.name ?? 'UNKNOWN';
  const message = MESSAGES[name] ?? 'That request failed.';
  return new AuthError(name, message);
}

function readTokens(output: InitiateAuthCommandOutput, email: string, previousRefreshToken?: string): SessionTokens {
  const result = output.AuthenticationResult;
  if (result?.IdToken === undefined || result.AccessToken === undefined) {
    // Challenges (new password, MFA) are not configured on this pool.
    throw new AuthError('CHALLENGE_REQUIRED', 'This account needs a sign-in step we do not support.');
  }
  const refreshToken = result.RefreshToken ?? previousRefreshToken;
  if (refreshToken === undefined) {
    throw new AuthError('NO_REFRESH_TOKEN', 'We could not start your session. Sign in again.');
  }
  return {
    idToken: result.IdToken,
    accessToken: result.AccessToken,
    refreshToken,
    expiresAt: Date.now() + (result.ExpiresIn ?? 3600) * 1000,
    email,
  };
}

export async function signUp(
  config: CognitoConfig,
  email: string,
  password: string,
): Promise<{ readonly confirmationRequired: boolean }> {
  try {
    const { SignUpCommand } = await sdk();
    const output = await (await client(config)).send(
      new SignUpCommand({
        ClientId: config.userPoolClientId,
        Username: email,
        Password: password,
        UserAttributes: [{ Name: 'email', Value: email }],
      }),
    );
    return { confirmationRequired: output.UserConfirmed !== true };
  } catch (error) {
    throw toAuthError(error);
  }
}

/**
 * Start a password reset. The pool is configured to hide whether an address has
 * an account, so this succeeds for an address that has none — the wording that
 * follows it must stay conditional or the screen becomes a way to ask whether a
 * given company has signed up.
 */
export async function forgotPassword(config: CognitoConfig, email: string): Promise<void> {
  try {
    const { ForgotPasswordCommand } = await sdk();
    await (await client(config)).send(
      new ForgotPasswordCommand({ ClientId: config.userPoolClientId, Username: email }),
    );
  } catch (error) {
    throw toAuthError(error);
  }
}

/** Finish a password reset with the emailed code. */
export async function confirmForgotPassword(
  config: CognitoConfig,
  email: string,
  code: string,
  newPassword: string,
): Promise<void> {
  try {
    const { ConfirmForgotPasswordCommand } = await sdk();
    await (await client(config)).send(
      new ConfirmForgotPasswordCommand({
        ClientId: config.userPoolClientId,
        Username: email,
        ConfirmationCode: code,
        Password: newPassword,
      }),
    );
  } catch (error) {
    throw toAuthError(error);
  }
}

export async function confirmSignUp(
  config: CognitoConfig,
  email: string,
  code: string,
): Promise<void> {
  try {
    const { ConfirmSignUpCommand } = await sdk();
    await (await client(config)).send(
      new ConfirmSignUpCommand({
        ClientId: config.userPoolClientId,
        Username: email,
        ConfirmationCode: code,
      }),
    );
  } catch (error) {
    throw toAuthError(error);
  }
}

export async function resendConfirmationCode(
  config: CognitoConfig,
  email: string,
): Promise<void> {
  try {
    const { ResendConfirmationCodeCommand } = await sdk();
    await (await client(config)).send(
      new ResendConfirmationCodeCommand({
        ClientId: config.userPoolClientId,
        Username: email,
      }),
    );
  } catch (error) {
    throw toAuthError(error);
  }
}

export async function signIn(
  config: CognitoConfig,
  email: string,
  password: string,
): Promise<SessionTokens> {
  try {
    const { AuthFlowType, InitiateAuthCommand } = await sdk();
    const output = await (await client(config)).send(
      new InitiateAuthCommand({
        AuthFlow: AuthFlowType.USER_PASSWORD_AUTH,
        ClientId: config.userPoolClientId,
        AuthParameters: { USERNAME: email, PASSWORD: password },
      }),
    );
    return readTokens(output, email);
  } catch (error) {
    throw toAuthError(error);
  }
}

export async function refreshSession(
  config: CognitoConfig,
  session: SessionTokens,
): Promise<SessionTokens> {
  try {
    const { AuthFlowType, InitiateAuthCommand } = await sdk();
    const output = await (await client(config)).send(
      new InitiateAuthCommand({
        AuthFlow: AuthFlowType.REFRESH_TOKEN_AUTH,
        ClientId: config.userPoolClientId,
        AuthParameters: { REFRESH_TOKEN: session.refreshToken },
      }),
    );
    return readTokens(output, session.email, session.refreshToken);
  } catch (error) {
    throw toAuthError(error);
  }
}
